import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { formatRelativeAge } from '../lib/format'
import { Icon } from './Badge'
import './FreshnessBanner.css'

/**
 * Data-freshness banner. Reads public.data_as_of.source_max_synced_at — the
 * Brain's own sync time of the copy the dashboard is showing — never our run
 * logs. The oldest of the four kinds counts: the screens join all four.
 *
 *   older than 4 h  -> warning role: "Order data last updated <relative>"
 *   older than 24 h -> danger role, same wording
 *
 * Nothing renders while loading, when fresh, or if the read fails (logged).
 *
 * TEST BUILD ONLY (acceptance 6): VITE_FRESHNESS_FIXED_AS_OF, an ISO
 * timestamp set at build time, replaces the database read so the amber and
 * red states can be shown without editing data_as_of. Never set for a
 * deployed build.
 */
const WARN_MS = 4 * 60 * 60 * 1000
const DANGER_MS = 24 * 60 * 60 * 1000
const FIXED_AS_OF: string | undefined = import.meta.env.VITE_FRESHNESS_FIXED_AS_OF

async function fetchAsOf(): Promise<string | null> {
  if (FIXED_AS_OF) return FIXED_AS_OF
  const { data, error } = await supabase.from('data_as_of').select('source_max_synced_at')
  if (error) {
    console.error('data_as_of read failed:', error.message)
    return null
  }
  if (!data || data.length === 0) return null
  return data.map((r) => r.source_max_synced_at).sort()[0] ?? null
}

export function FreshnessBanner() {
  const [asOf, setAsOf] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    let live = true
    void fetchAsOf().then((v) => {
      if (live) setAsOf(v)
    })
    // Keep the relative time honest on a screen left open.
    const t = setInterval(() => setNow(Date.now()), 60_000)
    return () => {
      live = false
      clearInterval(t)
    }
  }, [])

  if (!asOf) return null
  const age = now - new Date(asOf).getTime()
  if (!(age > WARN_MS)) return null
  const danger = age > DANGER_MS

  return (
    <p
      className={`esl-freshness esl-status--${danger ? 'danger' : 'warning'}`}
      role="status"
      title={new Date(asOf).toISOString()}
    >
      <Icon name={danger ? 'alert' : 'pending'} />
      Order data last updated {formatRelativeAge(age)}
    </p>
  )
}
